import * as THREE from "three";
import { isArrayEqual } from "../utils";

export const equalityThreshold = 0.001;
export const Y_COSMETIC_LIFT_AMOUNT = 0.1;
export const Y_LIFT_AMOUNT = 3;

export const POINT_TYPE = {
  POINT_WITH_MARKER: "POINT_WITH_MARKER",
  INVISIBLE_POINT: "INVISIBLE_POINT",
};

export const SNAP_POSITION_THRESHOLD = 0.25;

export const objectSelfPartKeyAndProp = {
  key: (object) => object.userData.isSelf,
  prop: {
    userData: {
      isSelf: true,
    },
  },
};

export function createGeometry(shapeType, params = [2, 10, 5]) {
  switch (shapeType) {
    case "cube":
      return <boxGeometry args={params} />;
    case "sphere":
      return <sphereGeometry args={[params[0], 32, 16]} />;
    case "cylinder":
      return <cylinderGeometry args={[params[0], params[0], params[1], 32]} />;
    case "cone":
      return <coneGeometry args={[params[0], params[1], 32]} />;
    case "pyramid":
      return <coneGeometry args={[params[0], params[1], 4]} />;
    case "torus":
      return <torusGeometry args={[params[0], params[1], 16, 100]} />;
    default:
      return <boxGeometry args={params} />;
  }
}

export const getMouseTargetPosition = (
  raycaster,
  mouse,
  camera,
  scene,
  followingSphereRef,
  distanceFromCamera
) => {
  raycaster.setFromCamera(mouse, camera);

  const intersects = raycaster.intersectObjects(scene.children, true);

  let targetPosition;
  let modelIntersect = intersects.find(
    (intersect) =>
      intersect.object !== followingSphereRef.current &&
      !objectSelfPartKeyAndProp.key(intersect.object)
  );

  if (modelIntersect) {
    targetPosition = modelIntersect.point;
  } else {
    const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const groundIntersection = new THREE.Vector3();

    if (raycaster.ray.intersectPlane(groundPlane, groundIntersection)) {
      targetPosition = groundIntersection;
    } else {
      targetPosition = camera.position
        .clone()
        .add(
          raycaster.ray.direction.clone().multiplyScalar(distanceFromCamera)
        );
    }
  }
  return targetPosition;
};

export const updateGroundPosition = (
  targetPosition,
  groundPosition,
  setGroundPosition
) => {
  const currentGround = [targetPosition.x, targetPosition.y, targetPosition.z];
  if (
    !groundPosition ||
    !isArrayEqual(currentGround, groundPosition, equalityThreshold)
  ) {
    setGroundPosition(currentGround);
  }
};

export const updateSpherePosition = (
  targetPosition,
  followingSphereRef,
  mouseLiftedPosition,
  setMouseLiftedPosition,
  positionLiftY,
  onSpherePositionChange,
  snapPosition
) => {
  const newLiftedPosition = targetPosition.clone();
  newLiftedPosition.y += positionLiftY;

  let newLiftedPositionArray = [
    newLiftedPosition.x,
    newLiftedPosition.y,
    newLiftedPosition.z,
  ];

  const isCloseToSnapPoint = isArrayEqual(
    snapPosition,
    newLiftedPositionArray,
    SNAP_POSITION_THRESHOLD
  );

  if (isCloseToSnapPoint) {
    newLiftedPositionArray = snapPosition;
    newLiftedPosition.set(snapPosition[0], snapPosition[1], snapPosition[2]);
  }

  if (
    !isArrayEqual(
      mouseLiftedPosition,
      newLiftedPositionArray,
      equalityThreshold
    )
  ) {
    onSpherePositionChange?.(newLiftedPosition);
  }
  setMouseLiftedPosition(newLiftedPositionArray);
  followingSphereRef.current.position.copy(newLiftedPosition);
};

export const updateXMarkPosition = (xMarkRef, groundPosition) => {
  if (xMarkRef.current && groundPosition && groundPosition.length === 3) {
    const currentPos = xMarkRef.current.position;
    if (
      currentPos.x !== groundPosition[0] ||
      currentPos.y !== groundPosition[1] ||
      currentPos.z !== groundPosition[2]
    ) {
      xMarkRef.current.position.set(
        groundPosition[0],
        groundPosition[1],
        groundPosition[2]
      );
    }
    xMarkRef.current.visible = true;
  }
};

export const updateConnectionLinePosition = (
  connectionLineRef,
  followingSphereRef,
  groundPosition
) => {
  if (
    connectionLineRef.current &&
    followingSphereRef.current &&
    groundPosition &&
    groundPosition.length === 3
  ) {
    const spherePos = followingSphereRef.current.position;
    const groundPos = new THREE.Vector3(
      groundPosition[0],
      groundPosition[1],
      groundPosition[2]
    );
    const spherePos3 = new THREE.Vector3(spherePos.x, spherePos.y, spherePos.z);

    const currentPoints =
      connectionLineRef.current.geometry.attributes.position;
    let needsUpdate = !currentPoints;

    if (currentPoints && currentPoints.array) {
      const array = currentPoints.array;
      needsUpdate =
        array[0] !== groundPos.x ||
        array[1] !== groundPos.y ||
        array[2] !== groundPos.z ||
        array[3] !== spherePos3.x ||
        array[4] !== spherePos3.y ||
        array[5] !== spherePos3.z;
    }

    if (needsUpdate) {
      const points = [groundPos, spherePos3];
      connectionLineRef.current.geometry.setFromPoints(points);
    }
    connectionLineRef.current.visible = true;
  }
};
